/**
 * Turn 6: Ask Pam (public diagnostic). Exam-prep only; never error UI on cap.
 * The answer stays on screen until the visitor moves on; the question is optional.
 */
import React, { useState } from 'react';
import PamMarkdown from '@/components/askpam/PamMarkdown';
import type { AskPamResult } from './types';
import { ASK_PAM_PROMPT_MAX } from './laneCPolicy';
import { publicDiagnosticAskPam } from './api';
import { ContinueButton } from './components/DiagnosticQuestion';

export interface AskPamTurnProps {
  sessionId: string;
  onComplete: (result: AskPamResult) => void;
}

export const AskPamTurn: React.FC<AskPamTurnProps> = ({ sessionId, onComplete }) => {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AskPamResult | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = prompt.trim();
    if (!trimmed || busy || result) return;
    setBusy(true);
    try {
      setResult(await publicDiagnosticAskPam(sessionId, trimmed));
    } finally {
      setBusy(false);
    }
  };

  const skip = () =>
    onComplete({
      turnId: 6,
      kind: 'ask_pam',
      completedAt: new Date().toISOString(),
      prompt: '',
      answerMarkdown: '',
      llmUsed: false,
    });

  return (
    <section
      className="rounded-2xl border-2 border-neutral-200 bg-white p-5 shadow-ledge sm:p-6"
      data-testid="public-diagnostic-ask-pam"
      aria-busy={busy}
    >
      <p className="text-[10px] font-bold uppercase tracking-widest text-secondary-700 mb-2">
        Ask Pam
      </p>
      <h2 className="font-display text-xl font-bold text-neutral-900 sm:text-2xl mb-2">
        One exam-prep question
      </h2>
      <p className="text-sm text-neutral-600 mb-4">
        Pam, a clinical supervisor, answers ASWB study questions. Not clinical or legal advice.
      </p>

      {!result ? (
        <form onSubmit={(e) => void submit(e)} className="space-y-3">
          <label className="block">
            <span className="sr-only">Your question for Pam</span>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value.slice(0, ASK_PAM_PROMPT_MAX))}
              maxLength={ASK_PAM_PROMPT_MAX}
              rows={4}
              disabled={busy}
              placeholder="e.g. How should I approach a FIRST question on the exam?"
              className="w-full rounded-xl border-2 border-neutral-200 px-3 py-2 text-base text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none"
              data-testid="ask-pam-input"
            />
          </label>
          <p className="text-right text-xs text-neutral-500">
            {prompt.length}/{ASK_PAM_PROMPT_MAX}
          </p>
          <button
            type="submit"
            disabled={busy || !prompt.trim()}
            className="btn-primary w-full"
            data-testid="ask-pam-submit"
          >
            {busy ? 'Pam is writing…' : 'Ask Pam'}
          </button>
          {busy && (
            <p role="status" className="flex items-center gap-2 text-sm text-neutral-600" data-testid="ask-pam-writing">
              <span className="flex gap-1" aria-hidden="true">
                <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce [animation-delay:0ms]" />
                <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce [animation-delay:120ms]" />
                <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce [animation-delay:240ms]" />
              </span>
              Pam is writing
            </p>
          )}
          <button
            type="button"
            onClick={skip}
            disabled={busy}
            className="w-full min-h-[44px] text-sm font-semibold text-neutral-600 hover:text-neutral-900"
            data-testid="ask-pam-skip"
          >
            Skip for now
          </button>
        </form>
      ) : (
        <div className="space-y-4">
          <div
            className="rounded-xl border border-neutral-100 bg-neutral-50 p-4 text-sm text-neutral-800"
            data-testid="ask-pam-answer"
            data-llm-used={result.llmUsed ? 'true' : 'false'}
            data-refused={result.refused ? 'true' : 'false'}
          >
            {!result.llmUsed && (
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-secondary-700">
                Pam&apos;s quick tip
              </p>
            )}
            <PamMarkdown content={result.answerMarkdown} />
          </div>
          <ContinueButton onClick={() => onComplete(result)} />
        </div>
      )}
    </section>
  );
};

export default AskPamTurn;
